import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Site-wide structured data. Mounted once in <Main>.
 * Per-page schemas (BreadcrumbList, Article, Service detail) live in <SeoHead>.
 *
 * We emit everything as an @graph so crawlers see one consistent entity graph.
 * This runs AFTER the <script type=application/ld+json> block in index.html
 * (which has the same graph) — they're intentionally aligned so JS-rendered
 * and non-JS crawls agree.
 */
const SITE_URL = "https://www.laviens.com";

type ServiceSpec = {
  name: string;
  description: string;
  serviceType: string;
  /** ISO 8601 duration — e.g. "PT60M" for 60 minutes, "PT90M" for 90. */
  durationISO?: string;
  /** Human-readable price range. Price ranges alone (no fixed value) */
  priceRange?: string;
  /** Fixed price in EUR. Emits Offer.price for rich results. */
  priceEur?: number;
};

const isoMinutes = (iso?: string): number | undefined => {
  if (!iso) return undefined;
  const m = iso.match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/);
  if (!m) return undefined;
  const h = m[1] ? parseInt(m[1], 10) : 0;
  const min = m[2] ? parseInt(m[2], 10) : 0;
  return h * 60 + min;
};

const SERVICES: ServiceSpec[] = [
  {
    name: "Massaggio Svedese",
    description:
      "Massaggio classico rilassante con tocco fluido, per sciogliere le tensioni e ripristinare il benessere generale.",
    serviceType: "Swedish Massage",
    durationISO: "PT60M",
  },
  {
    name: "Massaggio Decontratturante",
    description:
      "Massaggio mirato alle tensioni cervicali e lombari, per chi passa molte ore seduto o in tensione.",
    serviceType: "Deep Tissue Massage",
    durationISO: "PT60M",
  },
  {
    name: "Massaggio Linfodrenante",
    description:
      "Massaggio drenante (Metodo Vodder) per ridurre ritenzione idrica, gonfiori e gambe pesanti.",
    serviceType: "Lymphatic Drainage Massage",
    durationISO: "PT60M",
  },
  {
    name: "Massaggio Ayurvedico",
    description:
      "Pratica millenaria indiana: olii caldi e manovre lente per riequilibrare corpo e mente.",
    serviceType: "Ayurvedic Massage",
    durationISO: "PT90M",
  },
  {
    name: "Massaggio Californiano",
    description:
      "Massaggio avvolgente ed emozionale, ideale per chi cerca rilassamento profondo.",
    serviceType: "Californian Massage",
    durationISO: "PT90M",
  },
  {
    name: "Massaggio Hawaiano",
    description:
      "Lomi Lomi: manovre lunghe e fluide come onde, con olio caldo. Un'ora di rilassamento profondo di corpo e mente.",
    serviceType: "Hawaiian Massage",
    durationISO: "PT60M",
  },
  {
    name: "Aromaterapia",
    description:
      "Tocco del massaggio unito agli olii essenziali naturali per armonia fisica, mentale ed emotiva.",
    serviceType: "Aromatherapy",
    durationISO: "PT60M",
  },
  {
    name: "Skin Care Viso & Corpo",
    description:
      "Trattamenti su misura per fototipo, per mantenere la pelle sana, luminosa e curata.",
    serviceType: "Skin Care Treatment",
    durationISO: "PT60M",
  },
  {
    name: "Scrub Body",
    description:
      "Esfoliazione corpo completa per una pelle liscia, luminosa e pronta a ricevere idratazione profonda.",
    serviceType: "Body Scrub",
    priceEur: 50,
  },
  {
    name: "Relax Completo",
    description:
      "Percorso che unisce esfoliazione e massaggio per pelle rinnovata e rilassamento profondo.",
    serviceType: "Wellness Ritual",
    priceEur: 110,
  },
  {
    name: "Detox Corpo",
    description:
      "Trattamento mirato a stimolare la circolazione e favorire il drenaggio dei liquidi, per pelle liscia e corpo leggero.",
    serviceType: "Detox Body Ritual",
    priceEur: 100,
  },
  {
    name: "Deep Relax Experience",
    description:
      "Rituale intensivo che combina la rigenerazione della pelle con un'esperienza di massaggio profonda e avvolgente.",
    serviceType: "Deep Relaxation Ritual",
    priceEur: 160,
  },
  {
    name: "Dream Ritual",
    description:
      "Rituale completo viso-corpo che unisce esfoliazione e massaggio sincronizzato per un'esperienza unica.",
    serviceType: "Signature Spa Ritual",
    priceEur: 160,
  },
  {
    name: "Epilazione a Cera Braccia",
    description:
      "Epilazione delle braccia con cera calda delicata, rispettosa della pelle e con effetto di lunga durata.",
    serviceType: "Arm Waxing",
    durationISO: "PT30M",
    priceEur: 25,
  },
  {
    name: "Epilazione a Cera Gamba Completa",
    description:
      "Epilazione completa delle gambe — coscia e polpaccio — con cera calda, per una pelle liscia a lungo.",
    serviceType: "Full Leg Waxing",
    durationISO: "PT30M",
    priceEur: 40,
  },
  {
    name: "Epilazione a Cera Schiena",
    description:
      "Epilazione della schiena con cera calda, eseguita con attenzione al dettaglio e rispetto della pelle.",
    serviceType: "Back Waxing",
    durationISO: "PT30M",
    priceEur: 30,
  },
  {
    name: "Epilazione Brasiliana Donna",
    description:
      "Epilazione intima alla brasiliana per donna, eseguita con cera calda, massima discrezione e cura del dettaglio.",
    serviceType: "Brazilian Waxing — Women",
    durationISO: "PT45M",
    priceEur: 50,
  },
  {
    name: "Epilazione Brasiliana Uomo",
    description:
      "Epilazione intima alla brasiliana per uomo, eseguita con cera calda, massima discrezione e cura del dettaglio.",
    serviceType: "Brazilian Waxing — Men",
    durationISO: "PT1H30M",
    priceEur: 120,
  },
];

const buildOffers = () =>
  SERVICES.map((s) => {
    const minutes = isoMinutes(s.durationISO);
    return {
      "@type": "Offer" as const,
      priceCurrency: "EUR",
      availability: "https://schema.org/InStock",
      ...(s.priceEur != null ? { price: s.priceEur } : {}),
      ...(s.priceRange
        ? {
            priceSpecification: {
              "@type": "PriceSpecification",
              priceCurrency: "EUR",
              description: s.priceRange,
            },
          }
        : {}),
      itemOffered: {
        "@type": "Service",
        name: s.name,
        description: s.description,
        serviceType: s.serviceType,
        provider: { "@id": `${SITE_URL}/#localbusiness` },
        areaServed: [
          { "@type": "City", name: "Firenze" },
          { "@type": "City", name: "Sesto Fiorentino" },
        ],
        ...(minutes
          ? {
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                ...(s.priceEur != null ? { price: s.priceEur } : {}),
                eligibleDuration: {
                  "@type": "QuantitativeValue",
                  value: minutes,
                  unitCode: "MIN",
                },
              },
            }
          : {}),
      },
    };
  });

const SITE_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Laviens — Studio Olistico",
      legalName: "Laviens Studio Olistico",
      alternateName: ["Laviens", "Laviens Wellness", "Studio Olistico Laviens Regina"],
      url: `${SITE_URL}/`,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo512.png`,
        width: 512,
        height: 512,
      },
      image: `${SITE_URL}/og-cover.jpg`,
      telephone: "+39 349 246 8186",
      email: "info@laviens.com",
      founder: { "@type": "Person", name: "Regina" },
      foundingLocation: "Sesto Fiorentino, Firenze, Italia",
      areaServed: [
        { "@type": "City", name: "Firenze" },
        { "@type": "City", name: "Sesto Fiorentino" },
        { "@type": "AdministrativeArea", name: "Toscana" },
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: "+39 349 246 8186",
          contactType: "reservations",
          areaServed: "IT",
          availableLanguage: ["Italian"],
        },
      ],
      sameAs: [
        "https://www.treatwell.it/salone/laviens-wellness/",
        "https://www.instagram.com/laviens.io/",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Laviens — Studio Olistico, Firenze",
      description:
        "Studio olistico a Sesto Fiorentino (Firenze). Massaggi, aromaterapia e skin care con Regina.",
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "it-IT",
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${SITE_URL}/treatments?q={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": ["LocalBusiness", "HealthAndBeautyBusiness", "DaySpa"],
      "@id": `${SITE_URL}/#localbusiness`,
      name: "Laviens — Studio Olistico",
      image: [`${SITE_URL}/og-cover.jpg`, `${SITE_URL}/logo512.png`],
      url: `${SITE_URL}/`,
      telephone: "+39 349 246 8186",
      priceRange: "€€",
      currenciesAccepted: "EUR",
      paymentAccepted: "Cash, Credit Card, Debit Card",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Sesto Fiorentino",
        addressLocality: "Sesto Fiorentino",
        addressRegion: "FI",
        postalCode: "50019",
        addressCountry: "IT",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 43.8336896,
        longitude: 11.1806227,
      },
      hasMap:
        "https://www.google.com/maps/place/Studio+Olistico+-+Laviens+Regina",
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "08:00",
          closes: "18:00",
        },
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Saturday", "Sunday"],
          opens: "09:00",
          closes: "20:00",
        },
      ],
      parentOrganization: { "@id": `${SITE_URL}/#organization` },
      areaServed: [
        { "@type": "City", name: "Firenze" },
        { "@type": "City", name: "Sesto Fiorentino" },
        { "@type": "City", name: "Campi Bisenzio" },
        { "@type": "City", name: "Calenzano" },
        { "@type": "City", name: "Prato" },
      ],
      knowsLanguage: ["it-IT"],
      slogan: "Un rituale di benessere, pensato interamente per te.",
      sameAs: [
        "https://www.treatwell.it/salone/laviens-wellness/",
        "https://www.instagram.com/laviens.io/",
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "5",
        bestRating: "5",
        worstRating: "5",
      },
      review: [
        {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: { "@type": "Person", name: "Arianna" },
          reviewBody:
            "Regina mi ha subito fatta sentire a mio agio. Le ho spiegato le mie esigenze e ha capito come farsi rilassare sia fisicamente che mentalmente, con un fantastico massaggio di oltre 1 ora. Ci mette passione e dedizione. Raccomanda.",
        },
        {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: { "@type": "Person", name: "Polina Stetsenko" },
          inLanguage: "en",
          publisher: { "@type": "Organization", name: "Google" },
          reviewBody:
            "First time visitor! A heads-up: booking via Treatwell might be tricky as my appointment didn't show up on their end. Regardless, the staff was wonderful and managed to fit me in. I had a really bad backache and couldn't turn to the right at all. The massage was perfect, and the pain is completely gone. Thank you for the great service and friendly atmosphere. I will definitely return!",
        },
        {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: { "@type": "Person", name: "Stefania" },
          reviewBody:
            "Mi sono rivolta a Regina per una fastidiosa sensazione di contrattura al trapezio. Regina è accogliente e attenta. Con il suo massaggio total body mi sono sentita meglio fin da fine seduta.",
        },
        {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: { "@type": "Person", name: "Antonella" },
          reviewBody: "La adoro!",
        },
        {
          "@type": "Review",
          reviewRating: {
            "@type": "Rating",
            ratingValue: "5",
            bestRating: "5",
          },
          author: { "@type": "Person", name: "Letizia" },
          reviewBody:
            "Consiglio assolutamente il centro di Regina. Durante il trattamento è stata molto attenta e precisa. Locale pulito.",
        },
      ],
      makesOffer: buildOffers(),
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Catalogo Trattamenti Laviens",
        itemListElement: buildOffers(),
      },
    },
    {
      "@type": "Person",
      "@id": `${SITE_URL}/#regina`,
      name: "Regina",
      jobTitle: "Terapista olistica & fondatrice di Laviens",
      description:
        "Regina è la fondatrice di Laviens, studio olistico a Sesto Fiorentino (Firenze). Oltre 15 anni di pratica. Diplomata in massaggio svedese, decontratturante, linfodrenante, ayurvedico, californiano ed emozionale, con formazione in aromaterapia e skin care.",
      worksFor: { "@id": `${SITE_URL}/#organization` },
      affiliation: { "@id": `${SITE_URL}/#localbusiness` },
      knowsAbout: [
        "Massaggio olistico",
        "Massaggio svedese",
        "Massaggio decontratturante",
        "Massaggio linfodrenante (Metodo Vodder)",
        "Massaggio ayurvedico",
        "Massaggio californiano",
        "Massaggio emozionale",
        "Aromaterapia",
        "Skin care viso e corpo",
      ],
      knowsLanguage: ["Italian"],
      hasOccupation: {
        "@type": "Occupation",
        name: "Terapista olistica",
        occupationalCategory: "31-9011.00",
        skills:
          "Tecniche di massaggio olistico, ascolto del corpo, aromaterapia, skin care personalizzata.",
      },
      image: `${SITE_URL}/og-cover.jpg`,
      sameAs: ["https://www.instagram.com/laviens.io/"],
    },
  ],
};

const StructuredData: React.FC = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(SITE_GRAPH)}</script>
  </Helmet>
);

export default StructuredData;
